import { AppServices, type GetHistoryKlineReq } from 'network'
import { exchangeSocketService } from '../socket/ExchangeSocketService'
import { SocketEventName } from '../socket/SocketService'
import { useEnvStore } from './use-env'
import type { ResolutionString } from '../../../charting-library/charting_library'
import { useBaseInfoStore } from './use-base-info'

import { safeSecondToMillisecond } from 'utils'
import { useChangeSymbol, useConfigWatchEffect } from '../hooks'
import { HandicapType, ResolutionType, type KlineDirection, type ResolutionItem } from '../constants/kline'

export const useKlineStore = defineStore('kline', () => {
  const kline = ref<SocketResponse.HistoryKlineData[]>([])
  const currentSpotKline = ref<SocketResponse.KlineFormatData>()
  const previousSpotKline = ref<SocketResponse.KlineFormatData>()
  const klineDirection = ref<KlineDirection>('equal')
  const loading = ref(true)
  const loadingHistoryKline = ref(false)
  const route = useRoute()
  const currentSymbolId = ref<SymbolId | undefined>(route.query.symbol ? Number(route.query.symbol) : 22)
  const envStore = useEnvStore()
  const currentResolution = ref<ResolutionString>('15' as ResolutionString)
  const currentResolutionType = ref<ResolutionType>(1)
  const baseInfoStore = useBaseInfoStore()
  const handicapType = ref<HandicapType>(HandicapType.All)

  /** 获取历史K线的结束时间 */
  const toTime = ref(0)

  const resetHistoryKlineLoading = () => {
    loadingHistoryKline.value = true
  }

  const getHistoryKline = async (data: GetHistoryKlineReq): Promise<SocketResponse.KlineFormatData[]> => {
    const res = await AppServices.getHistoryKline(data)
    kline.value = res.data.kline
    currentSymbolId.value = Number(data.symbol)

    const newData = res.data.kline.map((item) => ({
      time: safeSecondToMillisecond(item.t),
      open: item.o,
      high: item.h,
      low: item.l,
      close: item.c,
      volume: item.v,
    }))

    loadingHistoryKline.value = false

    return newData
  }

  const formatSocketKline = (data: SocketResponse.SpotKlineData): SocketResponse.KlineFormatData => {
    return {
      time: safeSecondToMillisecond(data.t[0]),
      open: Number(data.o[0]),
      high: Number(data.h[0]),
      low: Number(data.l[0]),
      close: Number(data.c[0]),
      volume: Number(data.v[0]),
    }
  }

  const listenKline = () => {
    exchangeSocketService.onMessage<SocketResponse.KlineResponse>(SocketEventName.Kline, (response) => {
      if (Number(response.params.p) !== currentSymbolId.value) return
      currentSpotKline.value = formatSocketKline(response.data)
      loading.value = false
      loadingHistoryKline.value = false
      if (!previousSpotKline.value) {
        klineDirection.value = 'equal'
      } else if (currentSpotKline.value.close > previousSpotKline.value.close) {
        klineDirection.value = 'up'
      } else if (currentSpotKline.value.close < previousSpotKline.value.close) {
        klineDirection.value = 'down'
      }
      previousSpotKline.value = currentSpotKline.value
    })
  }

  /** 订阅盘口 */
  const subscribeHandicap = () => {
    if (!currentSymbolId.value) return
    exchangeSocketService.emit('Topic.sub', {
      type: 'depth',
      symbol: currentSymbolId.value,
      app_id: envStore.appId,
    })
  }

  /** 取消订阅盘口 */
  const unsubscribeHandicap = (symbolId: SymbolId) => {
    exchangeSocketService.emit('Topic.unsub', {
      type: 'depth',
      symbol: symbolId,
    })
  }

  /** 订阅实时成交 */
  const subscribeRealTimeTransaction = () => {
    if (!currentSymbolId.value) return
    exchangeSocketService.emit('Topic.sub', {
      type: 'order',
      symbol: currentSymbolId.value,
      app_id: envStore.appId,
    })
  }

  /** 取消订阅实时成交 */
  const unsubscribeRealTimeTransaction = (symbolId: SymbolId) => {
    exchangeSocketService.emit('Topic.unsub', {
      type: 'order',
      symbol: symbolId,
    })
  }

  /** 定于K线行情？好奇怪，和App沟通说是遗留问题，如果不订阅，深度图和实时成交不会触发返回 */
  const subscribeMarket = () => {
    if (!currentSymbolId.value) return
    exchangeSocketService.emit('Topic.sub', {
      type: 'market',
      symbol: currentSymbolId.value,
    })
  }

  /** 取消订阅K线行情 */
  const unsubscribeMarket = (symbolId: SymbolId) => {
    exchangeSocketService.emit('Topic.unsub', {
      type: 'market',
      symbol: symbolId,
    })
  }

  /** 订阅K线 */
  const subscribeKline = () => {
    useConfigWatchEffect(async () => {
      exchangeSocketService.emit('Topic.sub', {
        type: 'kline',
        symbol: currentSymbolId.value,
        resolution: currentResolution.value,
        app_id: envStore.appId,
      })
    })
  }

  const unsubscribeKline = (symbolId: SymbolId) => {
    exchangeSocketService.emit('Topic.unsub', {
      type: 'kline',
      symbol: symbolId,
      resolution: currentResolution.value,
      app_id: envStore.appId,
    })
  }

  const unsubscribeKlineWithResolution = (resolution: ResolutionString) => {
    exchangeSocketService.emit('Topic.unsub', {
      type: 'kline',
      symbol: currentSymbolId.value,
      resolution: resolution,
      app_id: envStore.appId,
    })
  }

  /** 设置订阅K线参数 */
  const setSubscribeKlineParams = (symbolId: SymbolId, resolution: ResolutionString) => {
    currentSymbolId.value = symbolId
    currentResolution.value = resolution
  }

  const setResolution = (resolutionConfig: ResolutionItem) => {
    resetHistoryKlineLoading()
    currentResolution.value = resolutionConfig.value as ResolutionString
    currentResolutionType.value = resolutionConfig.type
  }

  onBeforeUnmount(() => {})

  const getSpotFromSymbolName = () => {
    if (!currentSymbolId.value) return ''
    const data = baseInfoStore.getCoinStaticItemDataBySymbolId(currentSymbolId.value)
    if (!data) return ''
    return data.name.toUpperCase()?.split('_')[0]
  }

  const getSpotToSymbolName = () => {
    if (!currentSymbolId.value) return ''
    const data = baseInfoStore.getCoinStaticItemDataBySymbolId(currentSymbolId.value)
    if (!data) return ''
    return data.name.toUpperCase()?.split('_')[1]
  }

  const getSpotFullSymbol = () => {
    if (!currentSymbolId.value) return ''
    const data = baseInfoStore.getCoinStaticItemDataBySymbolId(currentSymbolId.value)
    if (!data) return ''
    return data.name.toUpperCase().replace('_', '/')
  }

  /** 价格精度 */
  const coinToPrecision = computed(() => {
    if (!currentSymbolId.value) return 2
    const data = baseInfoStore.getCoinStaticItemDataBySymbolId(currentSymbolId.value)
    if (!data) return 2
    return data.pricePrecision
  })

  const coinFromPrecision = computed(() => {
    if (!currentSymbolId.value) return 2
    const data = baseInfoStore.getCoinStaticItemDataBySymbolId(currentSymbolId.value)
    if (!data) return 2
    const coinFrom = data.coinFrom
    const coinFromData = baseInfoStore.getCoinInfoByItemId(coinFrom)
    return coinFromData?.itemDetail.numPrecision || 2
  })

  const setHandicapType = (type: HandicapType) => {
    handicapType.value = type
  }

  const reset = () => {
    resetHistoryKlineLoading()
    loading.value = true
    previousSpotKline.value = undefined
    currentSpotKline.value = undefined
    kline.value = []
    toTime.value = 0
  }

  const unsubscribeAll = (symbolId?: SymbolId) => {
    if (!symbolId) return
    unsubscribeHandicap(symbolId)
    unsubscribeRealTimeTransaction(symbolId)
    unsubscribeMarket(symbolId)
    unsubscribeKline(symbolId)
    reset()
  }

  const setToTime = (time: number) => {
    toTime.value = time
  }

  useChangeSymbol((_, oldSymbolId) => {
    unsubscribeAll(oldSymbolId)
  })

  return {
    kline,
    getHistoryKline,
    formatSocketKline,
    listenKline,
    klineDirection,
    currentSpotKline,
    loading,
    loadingHistoryKline,
    setSubscribeKlineParams,
    currentSymbolId,
    currentResolution,
    subscribeHandicap,
    subscribeKline,
    getSpotFromSymbolName,
    getSpotToSymbolName,
    getSpotFullSymbol,
    coinToPrecision,
    coinFromPrecision,
    handicapType,
    setHandicapType,
    setResolution,
    resetHistoryKlineLoading,
    toTime,
    setToTime,
    currentResolutionType,
    unsubscribeAll,
    subscribeMarket,
    subscribeRealTimeTransaction,
    unsubscribeKlineWithResolution,
  }
})
